import { CommonModule } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  OnInit,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { LOG_CONTENT, ToastService } from '../../../../index';
import type { Align } from '../../../common/types';
import { Pagination } from '../../../pagination/pagination.component';
import { addRowNumber } from '../../utils/row-data.utils';
import { BaseDataGrid } from '../../base/base-data-grid';
import {
  createStorageKey,
  DEFAULT_ALIGN,
  DEFAULT_ORDER_BY,
  DEFAULT_PAGE_NUMBER,
  DEFAULT_PAGE_SIZE,
  DEFAULT_QUERY,
  PAGE_SIZE_OPTIONS,
} from '../../consts';
import { GridHeader } from '../../header/grid-header.component';
import { DataGridStore } from '../../store/data-grid.store';
import { GridStorageService } from '../../store/storage.service';
import type { DataGridSearchOptions } from '../../types/pagination.type';
import { GridGallery } from '../../view/gallery/gallery.component';
import { GridTable } from '../../view/table/table.component';

@Component({
  host: {
    ngSkipHydration: 'true',
  },
  selector: 'lepi-ssp-data-grid',
  templateUrl: './ssp-data-grid.component.html',
  styleUrls: ['./ssp-data-grid.component.css'],
  standalone: true,
  imports: [
    GridTable,
    GridGallery,
    GridHeader,
    Pagination,
    FormsModule,
    CommonModule,
  ],
  providers: [DataGridStore],
})
export class ServerSidePaginationDataGrid extends BaseDataGrid implements OnInit {
  private readonly storageService = inject(GridStorageService);
  private readonly toastService = inject(ToastService);

  @ViewChild(GridHeader) header!: GridHeader;

  // Page size options for select dropdown
  protected readonly pageSizeOptions = PAGE_SIZE_OPTIONS;

  // Output 이벤트
  public readonly refresh = output<DataGridSearchOptions>();
  public readonly export = output<void>();

  // 검색 상태 시그널
  public pageNo = model<number>(DEFAULT_PAGE_NUMBER);
  public pageSize = model<number>(DEFAULT_PAGE_SIZE);
  public query = model<string>(DEFAULT_QUERY);
  public override align = model<Align>(DEFAULT_ALIGN);
  public override orderBy = model<string>(DEFAULT_ORDER_BY);

  // 타이틀 (선택적)
  public readonly title = input<string>('');

  // 등록 버튼 텍스트 (선택적) - 설정하면 등록 버튼 표시
  public readonly createButtonText = input<string>('');
  public readonly hideExcelButton = input<boolean>(false);

  // 등록 버튼 클릭 이벤트
  public readonly create = output<void>();

  public readonly groupBy = this.store.groupBy;

  public readonly totalItems = computed(() => {
    if (this.pageInfo()?.totalItems) {
      return this.pageInfo()?.totalItems;
    }
    return this._rawRowData()?.length || 0;
  });

  // 로컬스토리지 키 계산
  private readonly storageKey = computed(() => {
    const options = this._rawOptions();
    const customKey = options?.storageKey;
    return createStorageKey(customKey || 'ssp-grid');
  });

  // Computed: 검색 조건 객체 (query 제외)
  private readonly searchOptionsWithoutQuery = computed(() => ({
    pageNo: this.pageNo(),
    pageSize: this.pageSize(),
    align: this.align(),
    orderBy: this.orderBy(),
  }));

  // 이전 검색 조건 추적 (선택 초기화 판단용)
  private readonly previousSearchConditions = signal<string>('');

  constructor() {
    super();

    // Effect 1: Store 업데이트만 담당 (rowData 변경 시)
    effect(() => {
      const currentRowData = this._rawRowData();

      // rawData가 변경되면 processed data로 store 업데이트
      if (currentRowData) {
        this.updateStore();
      }
    });

    // Effect 2: 검색 조건 변경 시 로컬스토리지 저장 및 refresh emit
    effect(() => {
      const currentPageNo = this.pageNo();
      const currentPageSize = this.pageSize();
      const currentQuery = this.query();
      const currentAlign = this.align();
      const currentOrderBy = this.orderBy();

      const fullOptions = {
        pageNo: currentPageNo,
        pageSize: currentPageSize,
        query: currentQuery,
        align: currentAlign,
        orderBy: currentOrderBy,
      };

      // 로컬스토리지에 저장
      this.storageService.saveState(this.storageKey(), fullOptions);

      // 검색 조건 문자열 생성
      const searchConditionKey = `${currentPageNo}-${currentPageSize}-${currentQuery}-${currentAlign}-${currentOrderBy}`;
      const hasSearchConditionChanged =
        this.previousSearchConditions() !== searchConditionKey;

      if (hasSearchConditionChanged) {
        // refresh 이벤트 발생 (서버에 데이터 요청)
        this.refresh.emit(fullOptions);

        // 선택 초기화
        this.selecteds.set([]);
        this.previousSearchConditions.set(searchConditionKey);
      }
    });

    // Effect 3: 서버 응답 기준으로 유효하지 않은 페이지 번호 보정
    effect(() => {
      const pageInfo = this.pageInfo();
      const currentPageNo = this.pageNo();

      if (!pageInfo) return;

      const totalPages = Math.max(pageInfo.totalPages || 1, 1);

      if (currentPageNo > totalPages) {
        this.pageNo.set(totalPages);
      }
    });
  }

  ngOnInit(): void {
    // input signal이 resolve된 후 올바른 storageKey로 상태 복원
    const savedState = this.storageService.loadState(this.storageKey());
    if (savedState) {
      this.pageNo.set(savedState.pageNo);
      this.pageSize.set(savedState.pageSize);
      this.query.set(savedState.query);
      this.align.set(savedState.align);
      this.orderBy.set(savedState.orderBy);
    }
  }

  /**
   * 검색어 변경 시 호출 - 페이지를 1로 리셋
   */
  public onSearchQueryChange(newQuery: string): void {
    this.query.set(newQuery);
    if (newQuery) {
      this.pageNo.set(1);
    }
  }

  /**
   * 페이지 크기 변경 시 호출 - 페이지를 1로 리셋
   */
  public onPageSizeChange(newSize: number): void {
    this.pageSize.set(newSize);
    this.pageNo.set(1);
  }

  /**
   * @description 엑셀 내보내기 이벤트를 처리합니다.
   * fetcher가 정의되어 있을 경우 자동으로 전체 데이터를 불러와 내보냅니다.
   */
  public handleExport(): void {
    const fetcher = this.options()?.header?.fetcher;

    if (fetcher) {
      const params = {
        pageNo: 1,
        pageSize: this.totalItems() || Number.MAX_SAFE_INTEGER,
        query: this.query(),
        align: this.align(),
        orderBy: this.orderBy(),
      };

      const fileName =
        this.options()?.header?.excelFileName ||
        this.options()?.storageKey ||
        '데이터_추출';

      const context = new HttpContext().set(
        LOG_CONTENT,
        `[엑셀 다운로드] ${fileName}`
      );

      fetcher(params, context).subscribe({
        next: (res: { items: any[]; pageInfo: any }) => {
          const processedItems = addRowNumber(res.items, res.pageInfo);
          this.header.exportData(processedItems, fileName);
          this.toastService.success('엑셀 다운로드가 완료되었습니다.');
        },
        error: () => {
          this.toastService.error('데이터를 불러오는 중 오류가 발생했습니다.');
        },
      });
    } else {
      this.export.emit();
    }
  }
}
